import {
  useCallback, useEffect, useRef
} from 'react';
import PropTypes from 'prop-types';
import useShakaPlayer from '@/hooks/audio/useShakaPlayer';
import useAudioCompatibility from '@/hooks/audio/useAudioCompatibility';
import { importShakaDynamically, extractStreamedTimeRanges } from '@/helpers/audio';
import CONFIG from '@/config/global';

let shaka;

const loadShaka = async () => {
  shaka = await importShakaDynamically();
};

loadShaka();

const ShakaPlayer = ({
  onPlayerError,
  onPlayerLoading,
  onPlayerLoaded,
  onPlayerStateChange,
  onPlayerStateIdle,
  onPlayerStreaming,
  onPlayerBuffering,
  url,
  audioRefReady,
  audioRef,
  trackId
}) => {
  const { shakaSupported } = useAudioCompatibility();
  const {
    shakaPlayerRef,
    handleInitialisePlayer,
    extractBufferingTimeFromPlayer,
    getCurrentTrackStats,
    setPreviousTrackStats,
    resetCurrentTrackStats,
    setLoadedTrackKey,
    trackKey
  } = useShakaPlayer();

  // Create a reference to the current manifest URL
  const manifestUrlRef = useRef(null);
  const trackKeyRef = useRef(null);

  // Create a handler which will forcefully reload the audio.
  // It unloads the current url and then loads it in again.
  const reloadAudio = useCallback(
    ({ seek = 0 } = {}) => shakaPlayerRef.current.load(manifestUrlRef.current, seek).catch(onPlayerError),
    [onPlayerError, shakaPlayerRef]
  );

  // Create a handler to skip large gaps. Prevent default allows us to do this.
  const onPlayerLargeGap = useCallback((event) => { event.preventDefault(); }, []);
  // Extract the error detail from the event before calling the error handler
  const onPlayerErrorDetail = useCallback((event) => onPlayerError(event.detail), [onPlayerError]);

  // Handle mounting/unmounting
  useEffect(() => {
    const shakaPlayer = shakaPlayerRef.current;

    if (shakaSupported && audioRefReady && shaka.Player && !shakaPlayer) {
      // Create the player
      const shakaPlayerElement = new shaka.Player(audioRef.current);
      // Configure the player with out settings from config
      shakaPlayerElement.configure(CONFIG.audio.shakaPlayerConfig);

      // Pass the player and ui to the intialise prop to access it outside
      handleInitialisePlayer({
        player: shakaPlayerElement,
        reloadAudio
      });
    }
  }, [
    audioRefReady,
    audioRef,
    shakaSupported,
    shakaPlayerRef,
    reloadAudio,
    handleInitialisePlayer
  ]);

  // Attach and remove listeners from the audio element
  useEffect(() => {
    const shakaPlayer = shakaPlayerRef.current;

    if (shakaPlayer) {
      shakaPlayer.addEventListener('error', onPlayerErrorDetail);
      shakaPlayer.addEventListener('loading', onPlayerLoading);
      shakaPlayer.addEventListener('unloading', onPlayerLoaded);
      shakaPlayer.addEventListener('onstatechange', onPlayerStateChange);
      shakaPlayer.addEventListener('onstateidle', onPlayerStateIdle);
      shakaPlayer.addEventListener('largegap', onPlayerLargeGap);
      shakaPlayer.addEventListener('buffering', onPlayerBuffering);
      shakaPlayer.addEventListener('streaming', onPlayerStreaming);
    }

    return () => {
      if (shakaPlayer) {
        shakaPlayer.removeEventListener('error', onPlayerErrorDetail);
        shakaPlayer.removeEventListener('loading', onPlayerLoading);
        shakaPlayer.removeEventListener('unloading', onPlayerLoaded);
        shakaPlayer.removeEventListener('onstatechange', onPlayerStateChange);
        shakaPlayer.removeEventListener('onstateidle', onPlayerStateIdle);
        shakaPlayer.removeEventListener('largegap', onPlayerLargeGap);
        shakaPlayer.removeEventListener('buffering', onPlayerBuffering);
        shakaPlayer.removeEventListener('streaming', onPlayerStreaming);
      }
    };
  }, [
    shakaPlayerRef,
    onPlayerBuffering,
    onPlayerError,
    onPlayerErrorDetail,
    onPlayerLargeGap,
    onPlayerLoaded,
    onPlayerLoading,
    onPlayerStateChange,
    onPlayerStateIdle,
    onPlayerStreaming
  ]);

  // Handle loading audio. If the trackId changes, then load the audio.
  useEffect(() => {
    const trackHasChanged = (trackKey !== trackKeyRef.current);
    const manifestUrlHasChanged = (url !== manifestUrlRef.current);

    if (shakaPlayerRef.current) {
      if (trackHasChanged) {
        trackKeyRef.current = trackKey;
  
        setPreviousTrackStats({
          ...getCurrentTrackStats(),
          totalBufferTimeSeconds: extractBufferingTimeFromPlayer(),
          ...extractStreamedTimeRanges({ mediaElement: audioRef.current })
        });
        resetCurrentTrackStats();
      }
      
      if (manifestUrlHasChanged || trackHasChanged) {
        manifestUrlRef.current = url;

        if (url) {
          // Load the manifest url
          shakaPlayerRef.current.load(url)
            .then(() => {
              setLoadedTrackKey(trackId);
            })
            .catch(onPlayerError);
        } else {
          shakaPlayerRef.current.unload().catch(onPlayerError);
        }
      }
    }
  }, [
    url,
    audioRef,
    shakaPlayerRef,
    trackKey,
    trackId,
    extractBufferingTimeFromPlayer,
    getCurrentTrackStats,
    resetCurrentTrackStats,
    setPreviousTrackStats,
    setLoadedTrackKey,
    onPlayerError
  ]);

  return null;
};

ShakaPlayer.propTypes = {
  onPlayerError: PropTypes.func,
  onPlayerLoading: PropTypes.func,
  onPlayerLoaded: PropTypes.func,
  onPlayerStateChange: PropTypes.func,
  onPlayerStateIdle: PropTypes.func,
  onPlayerStreaming: PropTypes.func,
  onPlayerBuffering: PropTypes.func,
  audioRefReady: PropTypes.bool,
  audioRef: PropTypes.object,
  trackId: PropTypes.string,
  url: PropTypes.string
};

ShakaPlayer.defaultProps = { url: null };

ShakaPlayer.defaultProps = {
  onPlayerError: undefined,
  onPlayerLoading: undefined,
  onPlayerLoaded: undefined,
  onPlayerStateChange: undefined,
  onPlayerStateIdle: undefined,
  onPlayerStreaming: undefined,
  onPlayerBuffering: undefined,
  audioRefReady: false,
  audioRef: {},
  trackId: '',
  url: 'PropTypes.string'
};

export default ShakaPlayer;